import React, { useContext, useEffect, useState, useMemo } from "react";
import { TranslatorContext } from "../../../context/Translator";
import { Row, Col, Card, Modal, Form, Spinner } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { LabelFieldComponent, LabelTextareaComponent } from "../../../components/fields";
import { ButtonComponent } from "../../../components/elements";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import Select from "react-select";
import Swal from 'sweetalert2';
import { FaPlus } from "react-icons/fa";

export default function PurchaseCreatePage() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();
    const AuthToken = localStorage.getItem("authToken");

    const [loading, setLoading] = useState(true);
    const [vendors, setVendors] = useState([]);
    const [products, setProducts] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [errors, setErrors] = useState({});
    const [productStock, setProductStock] = useState({});
    const [forceUpdate, setForceUpdate] = useState(0);

    // Vendor Modal States
    const [showVendorModal, setShowVendorModal] = useState(false);
    const [vendorForm, setVendorForm] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        img: null
    });
    const [vendorErrors, setVendorErrors] = useState({});
    const [vendorLoading, setVendorLoading] = useState(false);

    // Get today's date in YYYY-MM-DD format
    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const [formData, setFormData] = useState({
        vendor_id: "",
        purchase_date: getTodayDate(),
        img: null,
        notes: "",
        products: [{ product_id: "", quantity: "", purchase_price: "", sale_price: "", online_price: "" }],
        payments: [{ payment_method_id: "", amount: "", payment_date: getTodayDate() }],
    });

    useEffect(() => {
        fetchInitialData();
    }, [AuthToken]);

    // Filter products based on selected vendor
    useEffect(() => {
        if (formData.vendor_id) {
            const selectedVendor = vendors.find(v => v.id == formData.vendor_id);
            if (selectedVendor && selectedVendor.products) {
                setProducts(selectedVendor.products);
            } else {
                setProducts([]);
            }
        } else {
            setProducts([]);
        }
    }, [formData.vendor_id, vendors]);

    const fetchInitialData = () => {
        axios
            .get(`${process.env.REACT_APP_API_URL}/purchases-create`, {
                headers: { Authorization: `Bearer ${AuthToken}` },
            })
            .then((res) => {
                setVendors(res.data.vendors);
                // setProducts(res.data.products); // Removed as products are now per vendor
                if (res.data.payment_methods) setPaymentMethods(res.data.payment_methods);
                else {
                    axios.get(`${process.env.REACT_APP_API_URL}/payment-methods`, {
                        headers: { Authorization: `Bearer ${AuthToken}` },
                    }).then(pmRes => setPaymentMethods(pmRes.data.payment_methods || []));
                }
            })
            .catch((err) => console.error(err))
            .finally(() => setLoading(false));
    };

    // Vendor Modal Handlers
    const openVendorModal = () => {
        setVendorForm({ name: '', email: '', phone: '', address: '', img: null });
        setVendorErrors({});
        setShowVendorModal(true);
    };

    const handleVendorChange = (e) => {
        const { name, value, type, files } = e.target;
        setVendorForm(prev => ({ ...prev, [name]: type === 'file' ? files[0] : value }));
        if (vendorErrors[name]) setVendorErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleVendorSubmit = async (e) => {
        e.preventDefault();
        setVendorLoading(true);
        setVendorErrors({});

        try {
            const formData = new FormData();
            for (let key in vendorForm) {
                if (vendorForm[key] !== null && vendorForm[key] !== '') {
                    formData.append(key, vendorForm[key]);
                }
            }

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/vendors-store`, formData, {
                headers: {
                    Authorization: `Bearer ${AuthToken}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                // Refresh vendors list
                const vendorsRes = await axios.get(`${process.env.REACT_APP_API_URL}/purchases-create`, {
                    headers: { Authorization: `Bearer ${AuthToken}` },
                });
                setVendors(vendorsRes.data.vendors);

                // Auto-select the newly created vendor
                const newVendor = response.data.vendor || response.data.data;
                if (newVendor && newVendor.id) {
                    setFormData(prev => ({ ...prev, vendor_id: newVendor.id }));
                }

                setShowVendorModal(false);
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: t('Vendor added successfully!'),
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true,
                });
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setVendorErrors(error.response.data.errors || {});
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'error',
                    title: t('Please check the form for errors'),
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true,
                });
            } else {
                console.error(error);
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'error',
                    title: t('Failed to add vendor'),
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true,
                });
            }
        } finally {
            setVendorLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleProductChange = (index, e) => {
        const { name, value } = e.target;
        const updatedProducts = [...formData.products];
        // Allow empty string for better UX
        const newValue = value === '' ? '' : (name === "quantity" || name.includes("price") ? Number(value) : value);
        updatedProducts[index][name] = newValue;
        setFormData((prev) => ({ ...prev, products: updatedProducts }));
    };

    const updatePreviousPrice = async (index, fieldName, productId) => {
        if (!productId) return;

        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/products/${productId}/last-prices`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            const updatedProducts = [...formData.products];
            const priceValue = res.data[fieldName] || 0;
            updatedProducts[index][fieldName] = priceValue;

            setFormData(prevForm => ({ ...prevForm, products: updatedProducts }));
            setForceUpdate(prev => prev + 1);

            setPreviousPrices(prev => ({
                ...prev,
                [productId]: { ...prev[productId], [fieldName]: priceValue }
            }));
        } catch (err) {
            console.error(err);
        }
    };

    const handleImageChange = (e) => {
        setFormData((prev) => ({
            ...prev,
            img: e.target.files[0],
        }));
    };

    const grandTotal = useMemo(() => {
        return formData.products.reduce((acc, p) => {
            const quantity = Number(p.quantity) || 0;
            const purchasePrice = Number(p.purchase_price) || 0;
            return acc + (quantity * purchasePrice);
        }, 0);
    }, [formData.products]);

    // Calculate total paid amount
    const totalPaid = useMemo(() => {
        return formData.payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    }, [formData.payments]);

    // Calculate remaining amount
    const getRemainingAmount = () => {
        return Math.max(0, grandTotal - totalPaid);
    };

    // Handle payment change with proper validation
    const handlePaymentChange = (index, e) => {
        const { name, value } = e.target;

        if (name === 'amount') {
            let newAmount = value === '' ? '' : Number(value);
            if (newAmount !== '' && (isNaN(newAmount) || newAmount < 0)) newAmount = 0;

            // Calculate total of all other payments
            let otherPaymentsTotal = 0;
            for (let i = 0; i < formData.payments.length; i++) {
                if (i !== index) {
                    otherPaymentsTotal += Number(formData.payments[i].amount) || 0;
                }
            }

            // Calculate maximum allowed for this payment
            const maxAllowed = grandTotal - otherPaymentsTotal;

            // If trying to enter amount greater than max allowed, show alert and revert
            if (newAmount !== '' && newAmount > maxAllowed) {
                Swal.fire({
                    icon: 'warning',
                    title: t("Invalid Amount"),
                    text: `${t("Amount cannot exceed")} ${maxAllowed}. ${t("Remaining amount is")} ${maxAllowed}`,
                    confirmButtonText: t("OK"),
                });
                return; // Just return without updating
            }

            // Update the payment
            const updatedPayments = [...formData.payments];
            updatedPayments[index] = {
                ...updatedPayments[index],
                [name]: newAmount === '' ? '' : newAmount
            };
            setFormData(prev => ({ ...prev, payments: updatedPayments }));
        } else {
            // Handle payment_method_id or payment_date
            const updatedPayments = [...formData.payments];
            updatedPayments[index] = {
                ...updatedPayments[index],
                [name]: value
            };
            setFormData(prev => ({ ...prev, payments: updatedPayments }));
        }
    };

    const addProductRow = () =>
        setFormData((prev) => ({
            ...prev,
            products: [...prev.products, { product_id: "", quantity: "", purchase_price: "", sale_price: "", online_price: "" }],
        }));

    const removeProductRow = (index) => {
        const updatedProducts = formData.products.filter((_, i) => i !== index);
        setFormData((prev) => ({ ...prev, products: updatedProducts }));
    };

    const addPaymentRow = () => {
        const remaining = getRemainingAmount();

        if (remaining <= 0) {
            Swal.fire({
                icon: 'info',
                title: t("Info"),
                text: t("Purchase is already fully paid"),
                confirmButtonText: t("OK"),
            });
            return;
        }

        setFormData(prev => ({
            ...prev,
            payments: [...prev.payments, {
                payment_method_id: "",
                amount: remaining, // This will auto-fill the remaining amount
                payment_date: getTodayDate()
            }]
        }));
    };

    const removePaymentRow = (index) => {
        if (formData.payments.length === 1) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t("At least one payment row is required"),
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true,
            });
            return;
        }

        const updatedPayments = formData.payments.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, payments: updatedPayments }));
    };

    const handleSubmit = () => {
        setErrors({});

        // Validate vendor is selected
        if (!formData.vendor_id) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: t("Please select a vendor"),
                showConfirmButton: false,
                timer: 2500,
                timerProgressBar: true,
            });
            return;
        }

        // Validate at least one product exists
        const hasValidProduct = formData.products.some(prod =>
            prod.product_id && prod.quantity && prod.quantity !== '' &&
            prod.purchase_price && prod.purchase_price !== ''
        );

        if (!hasValidProduct) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: t("Please add at least one valid product with quantity and purchase price"),
                showConfirmButton: false,
                timer: 2500,
                timerProgressBar: true,
            });
            return;
        }

        // Prepare data for submission - convert empty strings to 0
        const submitData = {
            ...formData,
            products: formData.products.map(prod => ({
                ...prod,
                quantity: prod.quantity === '' ? 0 : Number(prod.quantity),
                purchase_price: prod.purchase_price === '' ? 0 : Number(prod.purchase_price),
                sale_price: prod.sale_price === '' ? 0 : Number(prod.sale_price),
                online_price: prod.online_price === '' ? 0 : Number(prod.online_price),
            })),
            payments: formData.payments.map(payment => ({
                ...payment,
                amount: payment.amount === '' ? 0 : Number(payment.amount),
            }))
        };

        // Validate total payments equals grand total
        const totalPaidAmount = submitData.payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
        const grandTotalAmount = submitData.products.reduce((acc, p) => acc + (p.quantity * p.purchase_price), 0);

        if (totalPaidAmount > grandTotalAmount) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: t(`Total payments (${totalPaidAmount}) must equal Grand Total (${grandTotalAmount})`),
                showConfirmButton: false,
                timer: 2500,
                timerProgressBar: true,
            });
            return;
        }

        // Validate all payments have payment method selected if amount > 0
        const invalidPayments = submitData.payments.some(p => Number(p.amount) > 0 && !p.payment_method_id);
        if (invalidPayments) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t("Please select payment method for all payments with amount greater than 0"),
                showConfirmButton: false,
                timer: 2500,
                timerProgressBar: true,
            });
            return;
        }

        // Validate payment date is provided for payments with amount > 0
        const invalidPaymentDates = submitData.payments.some(p => Number(p.amount) > 0 && !p.payment_date);
        if (invalidPaymentDates) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t("Please select payment date for all payments with amount greater than 0"),
                showConfirmButton: false,
                timer: 2500,
                timerProgressBar: true,
            });
            return;
        }

        axios
            .post(`${process.env.REACT_APP_API_URL}/purchases`, submitData, {
                headers: { Authorization: `Bearer ${AuthToken}` },
            })
            .then(() => {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: t("Purchase created successfully!"),
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true,
                });
                navigate("/purchase-list");
            })
            .catch((err) => {
                if (err.response?.status === 422) setErrors(err.response.data.errors);
                else console.error(err);
            });
    };

    // Auto-populate first payment row amount with grand total
    useEffect(() => {
        if (formData.payments.length > 0 && grandTotal >= 0) {
            // If there's only one payment row, always sync the amount with grandTotal
            // This allows the first payment to update as products change
            if (formData.payments.length === 1) {
                const firstPaymentAmount = formData.payments[0]?.amount;
                
                // Always update if only one payment exists
                if (grandTotal > 0 && firstPaymentAmount !== grandTotal) {
                    setFormData(prev => ({
                        ...prev,
                        payments: [{
                            ...prev.payments[0],
                            amount: grandTotal,
                            payment_date: prev.payments[0].payment_date || getTodayDate()
                        }]
                    }));
                }
            }
        }
    }, [grandTotal]);

    const inputFullWidthStyle = { width: "100%", boxSizing: "border-box", backgroundColor: "white" };

    const isOnlineProduct = (productId) => {
        const product = products.find(p => p.id === productId);
        return product?.is_online === 1;
    };

    const [previousPrices, setPreviousPrices] = useState({});

    if (loading) return <PageLayout>{t("Loading...")}</PageLayout>;

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <Card className="mb-4 p-4">
                        <Card.Body>

                            {/* Back to Purchases Button */}
                            <ButtonComponent
                                className="btn btn-secondary mb-4"
                                onClick={() => navigate("/purchase-list")}
                                text={t("Back to Purchases")}
                            />

                            {/* Vendor and Purchase Date */}
                            <Row className="mb-4">
                                <Col xs={12} md={4} style={{ paddingLeft: '0.5rem' }}>
                                    <LabelFieldComponent
                                        type="date"
                                        label={t("Purchase Date")}
                                        name="purchase_date"
                                        value={formData.purchase_date}
                                        onChange={handleChange}
                                        error={errors.purchase_date}
                                        style={{ width: "100%", backgroundColor: "transparent" }}
                                    />
                                </Col><Col xs={12} md={4} style={{ paddingRight: '0.5rem' }}>
                                    <label style={{ fontWeight: 'bold', marginBottom: '4px', display: 'block' }}>
                                        {t("Vendor")} <span className="text-danger">*</span>
                                    </label>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <div style={{ flex: 1 }}>
                                            <Select
                                                options={vendors.map(vendor => ({ value: vendor.id, label: vendor.company_name }))}
                                                value={vendors
                                                    .filter(vendor => vendor.id === formData.vendor_id)
                                                    .map(vendor => ({ value: vendor.id, label: vendor.company_name }))}
                                                onChange={(selected) => {
                                                    const newVendorId = selected ? selected.value : "";
                                                    setFormData(prev => {
                                                        const updated = { ...prev, vendor_id: newVendorId };
                                                        // Clear product_id if not in new vendor's products
                                                        const selectedVendor = vendors.find(v => v.id == newVendorId);
                                                        const vendorProducts = selectedVendor ? selectedVendor.products.map(p => p.id) : [];
                                                        updated.products = prev.products.map(prod => {
                                                            if (!vendorProducts.includes(prod.product_id)) {
                                                                return { ...prod, product_id: "" };
                                                            }
                                                            return prod;
                                                        });
                                                        return updated;
                                                    });
                                                }}
                                                isClearable
                                                placeholder={t("Select Vendor")}
                                                styles={{
                                                    container: (provided) => ({ ...provided, width: "100%" }),
                                                    control: (provided) => ({ ...provided, height: "38px" }),
                                                }}
                                            />
                                        </div>
                                        <ButtonComponent
                                            className="btn btn-primary"
                                            onClick={openVendorModal}
                                            style={{ height: "38px", width: "38px", padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                            text={<FaPlus />}
                                        />
                                    </div>
                                    {errors.vendor_id && <span style={{ color: 'red', fontSize: '0.8rem' }}>{errors.vendor_id}</span>}
                                </Col>


                                <Col xs={12} md={4} style={{ paddingLeft: '0.5rem' }}>
                                    <LabelFieldComponent
                                        className="mc-label-field-input w-md h-sm mt-1"
                                        type="file"
                                        label={t("Upload Image")}
                                        name="img"
                                        onChange={handleImageChange}
                                        error={errors.img}
                                        style={{ width: "100%", backgroundColor: "transparent" }}
                                    />
                                </Col>
                            </Row>

                            {/* Products Section */}
                            <h4 className="mb-3">{t("Products")} <span className="text-danger">*</span></h4>
                            {formData.products.map((prod, index) => (
                                <Card
                                    className="mb-3 p-3"
                                    key={index}
                                    style={{ borderColor: "#ddd", backgroundColor: "#f9f9f9", borderRadius: "6px" }}
                                >
                                    <Row className="align-items-stretch">

                                        {/* Delete Button */}
                                        <Col xl={1} className="d-flex flex-column">
                                            <div className="">
                                                <ButtonComponent
                                                    className="btn btn-danger btn-sm w-70 mt-4"
                                                    onClick={() => removeProductRow(index)}
                                                    text="X"
                                                />
                                            </div>
                                        </Col>

                                        {/* Product */}
                                        <Col xl={3} className="d-flex flex-column">
                                            <div className="d-flex flex-column h-100">
                                                <label className="fw-bold mb-1">{t("Product")} <span className="text-danger">*</span></label>

                                                <div className="mt-1">
                                                    <Select
                                                        options={products
                                                            .filter(p => !formData.products.some((fp, i) => fp.product_id === p.id && i !== index))
                                                            .map(product => ({ value: product.id, label: product.name }))
                                                        }
                                                        value={
                                                            products
                                                                .filter(product => product.id === prod.product_id)
                                                                .map(product => ({ value: product.id, label: product.name }))[0] || null
                                                        }
                                                        onChange={async (selected) => {
                                                            const updatedProducts = [...formData.products];
                                                            updatedProducts[index].product_id = selected ? selected.value : "";
                                                            setFormData(prev => ({ ...prev, products: updatedProducts }));

                                                            if (selected) {
                                                                try {
                                                                    const res = await axios.get(
                                                                        `${process.env.REACT_APP_API_URL}/products/${selected.value}/last-prices`,
                                                                        { headers: { Authorization: `Bearer ${AuthToken}` } }
                                                                    );

                                                                    setPreviousPrices(prev => ({
                                                                        ...prev,
                                                                        [selected.value]: res.data
                                                                    }));

                                                                    setProductStock(prev => ({
                                                                        ...prev,
                                                                        [selected.value]: res.data.stock_quantity || 0
                                                                    }));
                                                                } catch (err) {
                                                                    console.error(err);
                                                                }
                                                            }
                                                        }}
                                                        isClearable
                                                        placeholder={t("Select Product")}
                                                    />
                                                </div>
                                            </div>
                                        </Col>

                                        {/* Quantity */}
                                        <Col xl={2} className="d-flex flex-column">
                                            <div className="d-flex flex-column h-100">
                                                <label className="fw-bold mb-1">{t("Quantity")} <span className="text-danger">*</span></label>

                                                <div className="mt-1">
                                                    <input
                                                        type="number"
                                                        name="quantity"
                                                        value={prod.quantity}
                                                        min={0}
                                                        placeholder={t("Enter quantity")}
                                                        onChange={(e) => {
                                                            handleProductChange(index, e);
                                                        }}
                                                        style={{
                                                            width: "100%",
                                                            height: "38px",
                                                            padding: '6px 8px',
                                                            borderRadius: '4px',
                                                            border: '1px solid #ced4da',
                                                            backgroundColor: 'white'
                                                        }}
                                                    />
                                                </div>

                                                {prod.product_id && productStock[prod.product_id] !== undefined && (
                                                    <div style={{ fontSize: "0.8rem", color: "#6c757d", marginTop: "4px" }}>
                                                        {t("Stock Quantity")}: {productStock[prod.product_id]}
                                                    </div>
                                                )}
                                            </div>
                                        </Col>

                                        {/* Purchase Price */}
                                        <Col xl={2} className="d-flex flex-column">
                                            <div className="d-flex flex-column h-100">
                                                <label className="fw-bold mb-1">{t("Purchase Price")} <span className="text-danger">*</span></label>

                                                <div className="mt-1">
                                                    <input
                                                        type="number"
                                                        name="purchase_price"
                                                        value={prod.purchase_price}
                                                        min={0}
                                                        step="0.01"
                                                        placeholder={t("Enter purchase price")}
                                                        onChange={(e) => {
                                                            handleProductChange(index, e);
                                                        }}
                                                        style={{
                                                            width: "100%",
                                                            height: "38px",
                                                            padding: '6px 8px',
                                                            borderRadius: '4px',
                                                            border: '1px solid #ced4da',
                                                            backgroundColor: 'white'
                                                        }}
                                                    />
                                                </div>

                                                <label style={{ fontSize: "0.8rem", marginTop: "4px", cursor: "pointer" }}>
                                                    <input
                                                        type="checkbox"
                                                        style={{ marginRight: "5px" }}
                                                        onChange={async (e) => {
                                                            if (e.target.checked && prod.product_id) {
                                                                await updatePreviousPrice(index, 'purchase_price', prod.product_id);
                                                            }
                                                        }}
                                                    />
                                                    {t("Previous Price")}
                                                </label>
                                            </div>
                                        </Col>
                                        {/* Sale Price */}
                                        <Col xl={2} className="d-flex flex-column">
                                            <div className="d-flex flex-column h-100">
                                                <label className="fw-bold mb-1">{t("Sale Price")} <span className="text-danger">*</span></label>

                                                <div className="mt-1">
                                                    <input
                                                        type="number"
                                                        name="sale_price"
                                                        value={prod.sale_price}
                                                        required={true}
                                                        min={0}
                                                        step="0.01"
                                                        placeholder={t("Enter sale price")}
                                                        onChange={(e) => {
                                                            handleProductChange(index, e);
                                                        }}
                                                        style={{
                                                            width: "100%",
                                                            height: "38px",
                                                            padding: '6px 8px',
                                                            borderRadius: '4px',
                                                            border: '1px solid #ced4da',
                                                            backgroundColor: 'white'
                                                        }}
                                                    />
                                                </div>

                                                <label style={{ fontSize: "0.8rem", marginTop: "4px", cursor: "pointer" }}>
                                                    <input
                                                        type="checkbox"
                                                        style={{ marginRight: "5px" }}
                                                        onChange={async (e) => {
                                                            if (e.target.checked && prod.product_id) {
                                                                await updatePreviousPrice(index, 'sale_price', prod.product_id);
                                                            }
                                                        }}
                                                    />
                                                    {t("Previous Price")}
                                                </label>
                                            </div>
                                        </Col>

                                        {/* Total */}
                                        <Col xl={2} className="d-flex flex-column">
                                            <div className="d-flex flex-column h-100">
                                                <label className="fw-bold mb-1">{t("Total")}</label>

                                                <div className="mt-1">
                                                    <input
                                                        type="number"
                                                        value={((Number(prod.quantity) || 0) * (Number(prod.purchase_price) || 0))}
                                                        readOnly
                                                        style={{
                                                            width: "100%",
                                                            height: "38px",
                                                            padding: '6px 8px',
                                                            borderRadius: '4px',
                                                            border: '1px solid #ced4da',
                                                            backgroundColor: '#e9ecef'
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </Col>

                                        {isOnlineProduct(prod.product_id) && (
                                            <Col xl={2}>
                                                <div className="d-flex flex-column h-100" style={{ marginLeft: '10px', }}>
                                                    <label className="fw-bold mb-1">{t("Online Price")} </label>
                                                    <div className="mt-1">
                                                        <input
                                                            type="number"
                                                            name="online_price"
                                                            min={0}
                                                            step="0.01"
                                                            value={prod.online_price}
                                                            placeholder={t("Enter online price")}
                                                            onChange={(e) => handleProductChange(index, e)}
                                                            style={{
                                                                width: "100%",
                                                                height: "38px",
                                                                padding: '6px 8px',
                                                                borderRadius: '4px',
                                                                border: '1px solid #ced4da',
                                                                backgroundColor: 'white',

                                                            }}
                                                        />
                                                    </div>
                                                    <label style={{ fontSize: "0.8rem", marginTop: "4px", cursor: "pointer" }}>
                                                        <input
                                                            type="checkbox"
                                                            style={{ marginRight: "5px" }}
                                                            onChange={async (e) => {
                                                                if (e.target.checked && prod.product_id) {
                                                                    await updatePreviousPrice(index, 'online_price', prod.product_id);
                                                                }
                                                            }}
                                                        />
                                                        {t("Previous Price")}
                                                    </label>
                                                </div>
                                            </Col>
                                        )}

                                    </Row>
                                </Card>
                            ))}
                            <Row className="mb-4">
                                <Col>
                                    <ButtonComponent
                                        className="btn btn-primary"
                                        onClick={addProductRow}
                                        text={t("Add Product")}
                                    />
                                </Col>
                                <Col className="text-end">
                                    <div>
                                        <strong>{t("Grand Total")}: {grandTotal}</strong>
                                    </div>
                                    <div className="mt-1">
                                        <span className={getRemainingAmount() > 0 ? "text-danger" : "text-success"}>
                                            <strong>{t("Pending Amount")}: {getRemainingAmount()}</strong>
                                        </span>
                                    </div>
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <Col xl={6}>
                                    <LabelTextareaComponent
                                        label={t("Notes")}
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleChange}
                                    />
                                </Col>
                                {/* Payment Section */}
                                <Col xl={6}>
                                    <h5 className="mb-3">{t("Payments")}</h5>
                                    {formData.payments.map((pay, index) => (
                                        <Card
                                            key={index}
                                            className="mb-3 p-3"
                                            style={{ borderColor: "#ddd", backgroundColor: "#f9f9f9", borderRadius: "6px" }}
                                        >
                                            <Row className="align-items-center gx-2" style={{ margin: 0 }}>
                                                <Col xs={4} style={{ paddingRight: 0 }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 0 }}>
                                                        <label
                                                            style={{ fontWeight: 'bold', fontSize: '0.9rem', marginBottom: '4px', lineHeight: '1.2' }}
                                                            htmlFor={`payment-method-${index}`}
                                                        >
                                                            {t("Payment Method")}
                                                        </label>
                                                        <select
                                                            id={`payment-method-${index}`}
                                                            name="payment_method_id"
                                                            value={pay.payment_method_id}
                                                            onChange={(e) => handlePaymentChange(index, e)}
                                                            style={{
                                                                width: "100%",
                                                                height: "38px",
                                                                padding: '6px 8px',
                                                                boxSizing: "border-box",
                                                                borderRadius: '4px',
                                                                border: '1px solid #ced4da',
                                                            }}
                                                        >
                                                            <option value="">{t("Select Method")}</option>

                                                            {paymentMethods
                                                                .filter(method =>
                                                                    !formData.payments.some(
                                                                        (p, i) => p.payment_method_id === method.id && i !== index
                                                                    )
                                                                )
                                                                .map((method) => (
                                                                    <option key={method.id} value={method.id}>
                                                                        {method.name}
                                                                    </option>
                                                                ))
                                                            }
                                                        </select>
                                                    </div>
                                                </Col>

                                                <Col xs={3} style={{ paddingRight: 0 }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 0 }}>
                                                        <label
                                                            style={{ fontWeight: 'bold', fontSize: '0.9rem', marginBottom: '4px', lineHeight: '1.2' }}
                                                            htmlFor={`amount-${index}`}
                                                        >
                                                            {t("Amount")}
                                                        </label>
                                                        <input
                                                            id={`amount-${index}`}
                                                            type="number"
                                                            name="amount"
                                                            value={pay.amount}
                                                            placeholder={t("Enter amount")}
                                                            onChange={(e) => handlePaymentChange(index, e)}
                                                            style={{
                                                                width: "100%",
                                                                height: "38px",
                                                                padding: '6px 8px',
                                                                boxSizing: "border-box",
                                                                borderRadius: '4px',
                                                                border: '1px solid #ced4da',
                                                                marginBottom: 0,
                                                            }}
                                                        />
                                                    </div>
                                                </Col>

                                                <Col xs={4} style={{ paddingRight: 0 }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 0 }}>
                                                        <label
                                                            style={{ fontWeight: 'bold', fontSize: '0.9rem', marginBottom: '4px', lineHeight: '1.2' }}
                                                            htmlFor={`payment-date-${index}`}
                                                        >
                                                            {t("Payment Date")} <span className="text-danger">*</span>
                                                        </label>
                                                        <input
                                                            id={`payment-date-${index}`}
                                                            type="date"
                                                            name="payment_date"
                                                            value={pay.payment_date}
                                                            onChange={(e) => handlePaymentChange(index, e)}
                                                            style={{
                                                                width: "100%",
                                                                height: "38px",
                                                                padding: '6px 8px',
                                                                boxSizing: "border-box",
                                                                borderRadius: '4px',
                                                                border: '1px solid #ced4da',
                                                                marginBottom: 0,
                                                            }}
                                                        />
                                                    </div>
                                                </Col>

                                                <Col xs={1} className="d-flex justify-content-center align-items-center" style={{ paddingLeft: '0.5rem', paddingTop: '20px' }}>
                                                    <ButtonComponent
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => removePaymentRow(index)}
                                                        text="X"
                                                        style={{ height: "38px", minWidth: '38px', padding: 0 }}
                                                        disabled={formData.payments.length === 1}
                                                    />
                                                </Col>
                                            </Row>
                                        </Card>
                                    ))}

                                    <ButtonComponent
                                        className="btn btn-primary btn-sm mt-1"
                                        onClick={addPaymentRow}
                                        text={t("Add Payment")}
                                        disabled={getRemainingAmount() <= 0}
                                    />
                                </Col>
                            </Row>

                            <ButtonComponent
                                className="mc-btn primary w-100 mt-3"
                                onClick={handleSubmit}
                                text={t("Create Purchase")}
                            />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Vendor Create Modal */}
            <Modal show={showVendorModal} onHide={() => setShowVendorModal(false)} size="lg" centered>
                <Modal.Header closeButton className="p-2">
                    <Modal.Title>{t('Add New Vendor')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleVendorSubmit}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('name')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={vendorForm.name}
                                        onChange={handleVendorChange}
                                        isInvalid={!!vendorErrors.name}
                                        placeholder={t('Enter vendor name')}
                                    />
                                    <Form.Control.Feedback type="invalid">{vendorErrors.name}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('email')}</Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={vendorForm.email}
                                        onChange={handleVendorChange}
                                        isInvalid={!!vendorErrors.email}
                                        placeholder={t('Enter email address')}
                                    />
                                    <Form.Control.Feedback type="invalid">{vendorErrors.email}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('phone')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="phone"
                                        value={vendorForm.phone}
                                        onChange={handleVendorChange}
                                        isInvalid={!!vendorErrors.phone}
                                        placeholder={t('Enter phone number')}
                                    />
                                    <Form.Control.Feedback type="invalid">{vendorErrors.phone}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('image')}</Form.Label>
                                    <Form.Control
                                        type="file"
                                        name="img"
                                        onChange={handleVendorChange}
                                        accept="image/*"
                                        isInvalid={!!vendorErrors.img}
                                    />
                                    <Form.Control.Feedback type="invalid">{vendorErrors.img}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>{t('address')}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="address"
                                value={vendorForm.address}
                                onChange={handleVendorChange}
                                isInvalid={!!vendorErrors.address}
                                placeholder={t('Enter address')}
                            />
                            <Form.Control.Feedback type="invalid">{vendorErrors.address}</Form.Control.Feedback>
                        </Form.Group>

                        {vendorErrors.general && <p className="text-danger">{vendorErrors.general}</p>}
                    </Modal.Body>
                    <Modal.Footer className="p-2">
                        <ButtonComponent className="btn btn-secondary" onClick={() => setShowVendorModal(false)}>
                            {t('cancel')}
                        </ButtonComponent>
                        <ButtonComponent className="btn btn-primary" type="submit" disabled={vendorLoading}>
                            {vendorLoading ? <><Spinner animation="border" size="sm" className="me-2" />{t('saving')}...</> : t('save')}
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>
        </PageLayout>
    );
}